import { createContext, type MutableRefObject } from 'react';
import type { Node as RFNode } from '@xyflow/react';

/**
 * The imperative API the bridge exposes from inside the React Flow
 * provider tree to consumers outside it (typically panels mounted in
 * Drawer portals — siblings of the engine's RF subtree).
 *
 * Mirrors RF's imperative methods 1:1 — no derived helpers. Consumers
 * compose primitives for higher-level gestures (e.g. "navigate to
 * frame" = setCenter + setNodes selection update).
 */
export interface CanvasImperative {
  /** Pan + optional zoom to center on (x, y). */
  setCenter: (
    x: number,
    y: number,
    opts?: { zoom?: number; duration?: number },
  ) => void;
  /** Set the entire viewport (pan + zoom) directly. */
  setViewport: (
    vp: { x: number; y: number; zoom: number },
    opts?: { duration?: number },
  ) => void;
  /** Zoom-and-pan to fit a set of nodes (defaults to all). */
  fitView: (opts?: {
    duration?: number;
    padding?: number;
    nodes?: { id: string }[];
  }) => void;
  /** Read the current viewport. */
  getViewport: () => { x: number; y: number; zoom: number };
  /** Mutate RF's node array via an updater. */
  setNodes: (updater: (nodes: RFNode[]) => RFNode[]) => void;
  /** Read the current RF node array. */
  getNodes: () => RFNode[];
}

/**
 * Holds the imperative ref for the canvas. The provider mounts at the
 * canvas-shell level (above both the engine and any portal-sibling
 * panels). The Imperative bridge inside the engine populates the ref;
 * the useImperative hook reads from it.
 *
 * Default: `null` — distinguishes "no provider above me" (returns
 * null) from "provider but bridge not yet mounted" (returns
 * { current: null }).
 */
export type ImperativeRef = MutableRefObject<CanvasImperative | null>;

export const ImperativeContext = createContext<ImperativeRef | null>(null);
